import { type AnyAction } from 'redux';
import actions from './actions';
import { type RootState } from '@/types/app';
import { type AuthConnectionFlow, type IAuthState } from '@/types/auth';

/**
 * The connection-banner flag each auth surface owns. Sign-up has one too
 * (`signUpConnectionError`) but is deliberately absent: TEAM-14320 AC6 requires its
 * behaviour unchanged, so nothing may clear it through CLEAR_CONNECTION_ERRORS.
 */
const CONNECTION_ERROR_FLAG: Record<AuthConnectionFlow, keyof IAuthState> = {
  login: 'loginConnectionError',
  forgotPassword: 'forgotPasswordConnectionError',
};

export const initialState: IAuthState = {
  loading: false,
  params: null,
  error: false,
  signUpConnectionError: false,
  loginConnectionError: false,
  loginFailed: false,
  forgotPasswordConnectionError: false,
  forgotPasswordSent: false,
  duplicateAccountError: false,
  guestUser: null,
  emailVerificationToggle: false,
};

export const auth = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case actions.SIGN_UP_REQUEST:
      return Object.assign({}, state, {
        loading: true,
        error: false,
        signUpConnectionError: false,
        duplicateAccountError: false,
      });
    case actions.LOGOUT:
      return Object.assign({}, state, {
        loading: true,
      });
    case actions.LOGIN_REQUEST:
      return Object.assign({}, state, {
        loading: true,
        params: action.params,
        loginConnectionError: false,
        loginFailed: false,
      });
    case actions.AUTH_SUCCESS:
    case actions.LOGOUT_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
      });
    // TEAM-14519: `loginFailed`, not the shared `error` flag, which sign-up reads.
    case actions.AUTH_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        loginFailed: true,
      });
    case actions.LOGOUT_FAILURE:
      return Object.assign({}, state, {
        loading: false,
      });
    case actions.SIGN_UP_FAILURE:
      return Object.assign({}, state, {
        error: true,
        loading: false,
      });
    case actions.SIGN_UP_CONNECTION_FAILURE:
      return Object.assign({}, state, {
        signUpConnectionError: true,
      });
    case actions.LOGIN_CONNECTION_FAILURE:
      return Object.assign({}, state, {
        loginConnectionError: true,
      });
    case actions.SIGN_UP_DUPLICATE_ACCOUNT:
      return Object.assign({}, state, {
        duplicateAccountError: true,
        loading: false,
      });
    case actions.RESET_PASSWORD:
      return Object.assign({}, state, {
        loading: true,
      });
    case actions.RESET_PASSWORD_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
      });
    case actions.RESET_PASSWORD_FAIL:
      return Object.assign({}, state, {
        loading: false,
        error: true,
      });
    case actions.FORGOT_PASSWORD:
      return Object.assign({}, state, {
        loading: true,
        forgotPasswordConnectionError: false,
        forgotPasswordSent: false,
      });
    case actions.FORGOT_PASSWORD_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
        forgotPasswordSent: true,
      });
    case actions.FORGOT_PASSWORD_FAIL:
      return Object.assign({}, state, {
        loading: false,
      });
    case actions.FORGOT_PASSWORD_CONNECTION_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        forgotPasswordConnectionError: true,
      });
    // Clears only the mounting form's own flag — a login form has no business wiping the
    // forgot-password banner. Sign-up is excluded entirely (see CONNECTION_ERROR_FLAG).
    case actions.CLEAR_CONNECTION_ERRORS:
      return Object.assign({}, state, {
        [CONNECTION_ERROR_FLAG[action.flow as AuthConnectionFlow]]: false,
      });
    case actions.CHECKOUT_AS_GUEST:
      return Object.assign({}, state, {
        loading: false,
        guestUser: action.payload,
      });
    case actions.TOGGLE_EMAIL_VERIFICATION:
      return Object.assign({}, state, {
        emailVerificationToggle: action.toggleEmailVerification,
        loading: false,
      });
    default:
      return state;
  }
};

export const authSliceSelector = (state: RootState) => state.app.auth;
